import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { selectTransferState } from '@/store/features/transfer/transfer-selectors';
import { fetchAccountBankRelationships } from '@/api/custom';
import {
  GetBankRelationshipResponseEntry,
  TransactionInfo,
  IRA_ACCOUNT_TYPES,
} from '@/util/transfer-util';
import { useNavigate } from 'react-router-dom';
import { setTransferState } from '@/store/features/transfer/transferSlice';
import {
  Form,
  Select,
  Button,
  Spin,
  Card,
  InputNumber,
  Alert,
  Modal,
  Space,
  Typography,
  Empty,
} from 'antd';
import { useTheme } from '@/contexts/ThemeContext';
import { produce } from 'immer';
import PlaidLinkModal from './PlaidLinkModal';
import { fetchAccountFinancialDetails } from '@/store/features/apex/apex-thunks';
import { useCustodianAccounts } from '@/hooks/useCustodianAccounts';
import { formatAccountType } from '@/util/account-type-format';
import {
  findDepositSourceAccount,
  formatDepositSourceLabel,
  getDepositEligiblePlaidAccounts,
  getPreferredDepositFundingPlaidId,
  isApprovedDepositBankRelationship,
} from './deposit-account-selection';

const { Option } = Select;
const { Title, Text } = Typography;

const TransferBankToPortfolio: React.FC = () => {
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const globalApexAccounts = useAppSelector(selectApexAccounts);
  const globalPlaidAccounts = useAppSelector(selectPlaidAccounts);
  const globalTransferState = useAppSelector(selectTransferState);
  const [plaidModalVisible, setPlaidModalVisible] = useState(false);

  const plaidAccounts = useMemo(
    () => getDepositEligiblePlaidAccounts(globalPlaidAccounts),
    [globalPlaidAccounts],
  );
  // The legacy per-account Apex list — only meaningful (and only used below)
  // for a legacy Apex firm. Always empty for a firm-data + Apex firm, since
  // that pipeline never runs there (see useCustodianAccounts).
  const legacyApexAccounts = useMemo(() => globalApexAccounts, [globalApexAccounts]);
  // Deposit-destination accounts to actually list/select from: the legacy
  // list above for a legacy Apex firm, or firm-data's blended custodian
  // accounts (real Apex ids via custodian_account_id) for a firm-data +
  // Apex firm. IRA contribution/distribution constraints below are only
  // ever available for the legacy list — firm-data-sourced destinations
  // conservatively skip IRA-specific fields rather than guess at them.
  const { accounts: resolvedDepositAccounts } = useCustodianAccounts();
  const hasLinkedPlaidAccounts = globalPlaidAccounts.length > 0;
  const hasDepositEligiblePlaidAccounts = plaidAccounts.length > 0;
  const navigate = useNavigate();
  const [bankRelationships, setBankRelationships] = useState<GetBankRelationshipResponseEntry>([]);
  const [bankRelationshipsLoading, setBankRelationshipsLoading] = useState(false);

  // use useRef to track requested accounts, avoid duplicate requests
  const requestedAccountIds = useRef(new Set<string>());

  useEffect(() => {
    legacyApexAccounts.forEach((account) => {
      // check if data is needed and not yet requested
      if (
        (!account.ira_contribution_constraints || !account.ira_distribution_constraints) &&
        !requestedAccountIds.current.has(account.id)
      ) {
        requestedAccountIds.current.add(account.id);
        dispatch(fetchAccountFinancialDetails(account.id));
      }
    });
  }, [legacyApexAccounts, dispatch]);

  const transaction = useMemo(
    () =>
      globalTransferState.state === 'Transferring'
        ? globalTransferState.transactions[0]
        : undefined,
    [globalTransferState],
  );

  useEffect(() => {
    if (hasDepositEligiblePlaidAccounts && globalTransferState.state === 'WaitingForFirstAccount') {
      const fundingPlaidId = getPreferredDepositFundingPlaidId(plaidAccounts, bankRelationships);
      if (!fundingPlaidId) {
        return;
      }
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: globalTransferState.transactions.map((transaction) => ({
            ...transaction,
            plaidAccountId: fundingPlaidId,
          })),
        }),
      );
    }
  }, [
    plaidAccounts,
    bankRelationships,
    globalTransferState,
    hasDepositEligiblePlaidAccounts,
    dispatch,
  ]);

  useEffect(() => {
    const accountId = transaction?.custodianAccountId;

    let cancelled = false;

    if (!accountId) {
      setBankRelationships([]);
      setBankRelationshipsLoading(false);
      return undefined;
    }

    setBankRelationships([]);
    setBankRelationshipsLoading(true);

    (async () => {
      try {
        const response = await fetchAccountBankRelationships(accountId);
        if (cancelled) {
          return;
        }
        setBankRelationships(
          response.bank_relationships.filter(
            (a) => a.state === 'APPROVED' || a.state === 'PENDING',
          ),
        );
      } catch (e) {
        if (cancelled) {
          return;
        }
        setBankRelationships([]);
        Modal.error({
          title: 'Network Error',
          content: `Unable to fetch bank relationships. Please try again later. Details: ${e}`,
        });
      } finally {
        if (!cancelled) {
          setBankRelationshipsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [transaction?.custodianAccountId]);

  const noDepositAccountCopy = hasLinkedPlaidAccounts
    ? {
        title: 'No Deposit-Eligible Bank Accounts',
        description:
          'To make a deposit, link a checking or savings account that can be used for deposits.',
        button: 'Link Checking or Savings Account',
      }
    : {
        title: 'No External Bank Accounts Connected',
        description: 'To make a deposit, you need to connect an external bank account first.',
        button: 'Link External Account',
      };

  const preferredFundingPlaidId = useMemo(
    () => getPreferredDepositFundingPlaidId(plaidAccounts, bankRelationships),
    [plaidAccounts, bankRelationships],
  );

  useEffect(() => {
    if (!transaction || transaction.kind !== 'deposit' || !preferredFundingPlaidId) {
      return;
    }

    const selectedBankRelationship = bankRelationships.find(
      (b) => b.plaid_id === transaction.plaidAccountId,
    );

    if (isApprovedDepositBankRelationship(selectedBankRelationship)) {
      return;
    }

    if (transaction.plaidAccountId === preferredFundingPlaidId) {
      return;
    }

    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.plaidAccountId = preferredFundingPlaidId;
          }),
        ],
      }),
    );
  }, [preferredFundingPlaidId, bankRelationships, dispatch, transaction]);

  const handlePlaidSuccess = () => {
    // Refresh the page or trigger a data refresh
    window.location.reload();
  };

  if (!hasDepositEligiblePlaidAccounts) {
    const containerClass = `transfer-deposit-container ${theme}-theme`;

    return (
      <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div>
                  <Title level={4} style={{ marginBottom: 16 }}>
                    {noDepositAccountCopy.title}
                  </Title>
                  <Text
                    type="secondary"
                    style={{ fontSize: 16, display: 'block', marginBottom: 24 }}
                  >
                    {noDepositAccountCopy.description}
                  </Text>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setPlaidModalVisible(true)}
                    style={{
                      borderRadius: 8,
                      padding: '8px 24px',
                      height: 'auto',
                    }}
                  >
                    {noDepositAccountCopy.button}
                  </Button>
                </div>
              }
            />
          </div>
        </Card>

        <PlaidLinkModal
          visible={plaidModalVisible}
          onClose={() => setPlaidModalVisible(false)}
          onSuccess={handlePlaidSuccess}
        />
      </div>
    );
  }

  if (!transaction || transaction.kind !== 'deposit' || transaction.plaidAccountId === undefined) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="No Data" description="No data available" type="warning" showIcon />
      </div>
    );
  }

  // IRA contribution fields below are only meaningful for the legacy
  // per-account Apex list, which is the only source that carries
  // ira_contribution_constraints. A firm-data-sourced destination account
  // simply won't match here, so accountType/requireIra/validIraTypes fall
  // back to their empty defaults rather than guessing at IRA eligibility.
  const apexAccount = legacyApexAccounts.find((a) => a.id === transaction.custodianAccountId);

  const accountType = apexAccount?.type ?? '';
  const requireIra = IRA_ACCOUNT_TYPES.has(accountType as any);

  const validIraTypes = apexAccount?.ira_contribution_constraints?.valid_types ?? [];

  const acceptableYears: number[] = [];

  if (
    apexAccount?.ira_contribution_constraints?.only_allow_current_year ||
    !validIraTypes.find((t) => t.type == transaction.iraContributionType)?.previous_year_allowed
  ) {
    acceptableYears.push(new Date().getFullYear());
  } else {
    const thisYear = new Date().getFullYear();
    acceptableYears.push(thisYear);
    acceptableYears.push(thisYear - 1);
  }

  const selectedBankRelationship = bankRelationships.find(
    (b) => b.plaid_id === transaction.plaidAccountId,
  );
  const selectedBankRelationshipApproved =
    isApprovedDepositBankRelationship(selectedBankRelationship);
  const depositReadinessWarning = selectedBankRelationship
    ? {
        message: 'Bank Account Pending Approval',
        description:
          'This bank account is still being approved for deposits. Select an approved bank account or try again later.',
      }
    : {
        message: 'Bank Account Not Ready for Deposits',
        description:
          'This linked bank account is not ready for deposits. Select an approved checking or savings account or try again later.',
      };

  const handleAmountChange = (value: number | null) => {
    if (value !== null) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.amount = value;
            }),
          ],
        }),
      );
    }
  };

  const handleFromAccountChange = (plaidId: string) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.plaidAccountId = plaidId;
          }),
        ],
      }),
    );
  };

  const handleToAccountChange = (accountId: string) => {
    const selectedAccount = resolvedDepositAccounts.find((a) => a.id === accountId);
    if (selectedAccount) {
      dispatch(
        setTransferState({
          state: 'Transferring',
          transactions: [
            produce(transaction, (t) => {
              t.custodianAccountId = accountId;
              // TODO: 需要适配新的数据结构 - 账户类型检查需要基于新的类型字段
              if (!IRA_ACCOUNT_TYPES.has(selectedAccount.type)) {
                t.iraContributionType = undefined;
              }
            }),
          ],
        }),
      );
    }
  };

  const handleIraYearChange = (year: number) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.iraContributionYear = year;
          }),
        ],
      }),
    );
  };

  const handleIraTypeChange = (type: string) => {
    dispatch(
      setTransferState({
        state: 'Transferring',
        transactions: [
          produce(transaction, (t) => {
            t.iraContributionType = type as TransactionInfo['iraContributionType'];
          }),
        ],
      }),
    );
  };

  const handleCancel = () => {
    navigate('/home/wealth');
  };

  const containerClass = `transfer-deposit-container ${theme}-theme`;

  return (
    <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <Spin spinning={bankRelationshipsLoading} size="large">
        <Card title="Transfer from Bank to Portfolio">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Form layout="vertical" size="large">
              {/* Amount Input */}
              <Form.Item label="Amount">
                <InputNumber
                  size="large"
                  style={{ width: '100%' }}
                  value={transaction.amount}
                  onChange={handleAmountChange}
                  formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                  min={0}
                  precision={2}
                />
              </Form.Item>

              {/* From Account */}
              <Form.Item label="From">
                <Select
                  value={transaction.plaidAccountId}
                  onChange={handleFromAccountChange}
                  size="large"
                  placeholder="Select bank account"
                >
                  {bankRelationships.map((relationship) => {
                    if (!relationship.plaid_id) {
                      return null;
                    }

                    const sourceAccount = findDepositSourceAccount(
                      plaidAccounts,
                      relationship.plaid_id,
                    );

                    return (
                      <Option key={relationship.plaid_id} value={relationship.plaid_id}>
                        {formatDepositSourceLabel(relationship, sourceAccount)}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              {/* To Account */}
              <Form.Item label="To">
                <Select
                  value={transaction.custodianAccountId}
                  onChange={handleToAccountChange}
                  size="large"
                  placeholder="Select investment account"
                >
                  {resolvedDepositAccounts.map((account) => (
                    <Option key={account.id} value={account.id}>
                      {`${formatAccountType(account.type)} (${account.name})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              {/* IRA Contribution Year */}
              {requireIra && (
                <Form.Item label="IRA Contribution Year">
                  <Select
                    value={transaction.iraContributionYear}
                    onChange={handleIraYearChange}
                    size="large"
                    placeholder="Select year"
                  >
                    {acceptableYears.map((year) => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {/* IRA Contribution Type */}
              {requireIra && (
                <Form.Item label="IRA Contribution Type">
                  <Select
                    value={transaction.iraContributionType}
                    onChange={handleIraTypeChange}
                    size="large"
                    placeholder="Select contribution type"
                  >
                    {validIraTypes.map((type) => (
                      <Option key={type.type} value={type.type!}>
                        {(type.type ?? '-').split('_').join(' ')}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {/* Warning Message */}
              {!selectedBankRelationshipApproved && (
                <Alert
                  message={depositReadinessWarning.message}
                  description={depositReadinessWarning.description}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {/* Review and Cancel Buttons */}
              <Form.Item>
                <Space size="middle">
                  <Button
                    type="primary"
                    size="large"
                    disabled={transaction.amount === 0 || !selectedBankRelationshipApproved}
                    onClick={() => navigate('/home/transfer')}
                  >
                    Review Transfer
                  </Button>
                  <Button size="large" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      </Spin>
    </div>
  );
};

export default TransferBankToPortfolio;
